import { memo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHardwareProviderState } from '../../../../context/hardware';
import { useFullReset, useKioskBannerImages, useKioskState } from '../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../context/Localization/localization';
import { useDevelopmentDispatch, useDevelopmentState } from '../../../../context/Development/Development';
import { DevelopmentDispatchTypes } from '../../../../context/Development/Development.types';
import { GMCarousel } from '../../../../controls/Carousel';
import { IconButton } from '../../../../controls/IconButton/IconButton';
import { ImageHelper } from '../../../../controls/ImageHelper/ImageHelper';
import { LocalizationButtons } from '../../../../controls/LocalizationButtons/LocalizationButtons';
import { ThemeImage } from '../../../../controls/ThemeImage/ThemeImage';
import { isDevelopment } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { isVSAKiosk } from '../../../../helpers/EnvironmentFlags/EnvironmentFlags';
import { AdminRoutePaths, AppRoutePaths, AppointmentRoutePaths, PathName } from '../../../../helpers/Routes';
import { GMButton } from '../../../../controls/Button';
import { GMDialog } from '../../../../controls/Dialog';
import { GMSizedIcon } from '../../../../controls/Icon';
import { CTAButton } from '../../../../controls/ButtonBase';
import { VersionInfo } from '../../../../controls/VersionInfo/VersionInfo';
import { BackIcon } from '../BackIcon/BackIcon';
import { CartIcon } from '../CartIcon/CartIcon';
import css from '../Header.module.scss';
import type { KioskHeaderProps } from './KioskHeader.types';

export const KioskHeader = memo((props: KioskHeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const resetKiosk = useFullReset();
    const { isLinux } = useHardwareProviderState();
    const Localization = useLocalization();
    const { isDev } = useDevelopmentState();
    const developmentDispatch = useDevelopmentDispatch();
    const {
        dealer: { themeBannerEnabled = true, name: dealerName, stateEstimateEnabled },
        kioskType,
    } = useKioskState();
    const kioskBannerImages =
        useKioskBannerImages()
            .map((src) => (
                <ImageHelper imageProps={{
                    src,
                    title: Localization.formatString(Localization.BannerImage, dealerName) as string,
                }}
                />
            ));
    const isAdmin = location.pathname.includes(AppRoutePaths.Admin);
    const [showVersionInfo, setShowVersionInfo] = useState<boolean>(false);

    const onHeaderClick = () => {
        if (!isAdmin) {
            if (!isLinux || (isLinux && !isVSAKiosk(kioskType))) {
                navigate(AppRoutePaths.Admin);
            }
            else {
                navigate(PathName(AppRoutePaths.Admin, AdminRoutePaths.KioskInformation));
            }
        }
    };

    return (
        <div>
            <GMDialog
                ariaLabel={Localization.VersionInformation}
                open={showVersionInfo}
                title={Localization.VersionInformation}
                actions={[
                    <GMButton
                        id={Localization.Close}
                        title={Localization.Close}
                        ariaLabel={Localization.Close}
                        onClick={() => setShowVersionInfo(false)}
                    >
                        {Localization.Close}
                    </GMButton>,
                ]}
            >
                <VersionInfo />
            </GMDialog>
            {!(location.pathname.includes('/' + AppointmentRoutePaths.DisclaimerTabs) && stateEstimateEnabled) ? (
                <>
                    <div onClick={onHeaderClick} className={css.themeIconBanner}>
                        <ThemeImage themeImageType={'kioskHeaderLogo'} />
                    </div>
                    {!props.suppressBanner ? (
                        <GMCarousel
                            name={'Carousel'}
                            items={[themeBannerEnabled && <ThemeImage themeImageType={'landingPageBanner'} />, ...kioskBannerImages]}
                        />
                    ) : null}
                </>
            ) : null}
            <nav className={css.nav}>
                <div className={css.leftIcons}>
                    <div>
                        <IconButton
                            headerIcon
                            slim
                            onClick={resetKiosk}
                            sizedIcon={<GMSizedIcon iconType={'Home'} iconSize={'custom'} />}
                            ariaLabel={Localization.Home}
                            id={Localization.Home}
                        />
                    </div>
                    <div>
                        <BackIcon slim />
                    </div>
                </div>
                <div className={css.rightIcons}>
                    {isDevelopment() ? (
                        <div style={{
                            marginLeft: '1rem',
                            marginRight: '1rem',
                        }}
                        >
                            <CTAButton
                                hideIcon
                                key={'DevToggle'}
                                className={css.devButton}
                                onClick={() => {
                                    developmentDispatch({
                                        type: DevelopmentDispatchTypes.SetIsDev,
                                        payload: !isDev,
                                    });
                                }}
                                label={isDev ? 'Dev. On' : 'Dev. Off'}
                                styleType={'secondary'}
                                id={isDev ? 'Dev. On' : 'Dev. Off'}
                                ariaLabel={isDev ? 'Dev. On' : 'Dev. Off'}
                            />
                        </div>
                    ) : null}
                    {!props.suppressLocalizationButtons && (
                        <div className={css.localization}>
                            <LocalizationButtons />
                        </div>
                    )}
                    {isLinux && props.showSettingsButton && (
                        <div>
                            <IconButton
                                headerIcon
                                slim
                                onClick={() => navigate(PathName(AppRoutePaths.Admin, AdminRoutePaths.SetupLogin))}
                                sizedIcon={<GMSizedIcon iconType={'Settings'} iconSize={'custom'} />}
                                ariaLabel={Localization.ClickToOpenSettings}
                                id={Localization.ClickToOpenSettings}
                            />
                        </div>
                    )}
                    <CartIcon
                        onClick={props.onCartClick}
                        count={props.cartCount}
                        slim
                    />
                </div>
            </nav>
        </div>
    );
});
